import { useAxios } from '@plugins/axios';
import { useSnackbar } from '@/composables/useSnackbar';
import type { AxiosResponse } from 'axios';
import type { ResponseErrorType } from '@/types/ResponseErrorType';
import type { HandleRequestParamsType } from '@/types/composables/RequestTypes';
import { ResponseStatusEnum } from '@/types/enums/ResponseStatusEnum';
import { isResponseErrorType } from './useTypeGuards';
import { type SnackbarMessageType } from '@/types/SnackbarMessageType';
import i18n from '@plugins/i18n';

export const handleRequest = async ({
    method,
    endpoint,
    successMessageKey,
    successMessageParams,
    data,
    params,
    headers,
    onSuccess,
    onError,
    canShowSnackbar = true,
}: HandleRequestParamsType): Promise<AxiosResponse | ResponseErrorType> => {
    const { handleSnackbarError, handleSnackbarSuccess, showSnackbarMessage } = useSnackbar();

    try {
        const response = await useAxios()[method](endpoint, data, { headers, params });
        if (onSuccess) {
            onSuccess(response);
        }

        if (canShowSnackbar && (successMessageKey || response.data.message)) {
            let successMessage = response.data.message;
            if (successMessageKey) {
                successMessage = i18n.global.t(successMessageKey, successMessageParams || {});
            }

            const message: SnackbarMessageType = handleSnackbarSuccess(successMessage);
            showSnackbarMessage(message);
        }

        return response;
    } catch (error: any) {
        console.error(error);
        if (isResponseErrorType(error.response) && error.response.data.message) {
            if (onError) {
                onError();
            }
            if (canShowSnackbar && error.response.status !== ResponseStatusEnum.NotFound) {
                showSnackbarMessage(handleSnackbarError(error.response.data.message));
            }
        }

        return error.response;
    }
};
